/* assets/custom-product-zoom.js */
/* Author: Sabastian Bradley */

(() => {
  const DEBUG = false;

  // Target zoom "feel". Actual zoom is clamped so it won't exceed available pixels.
  const ZOOM_SCALE_TARGET = 2.6;

  const desktop = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  if (!desktop) return;

  const log = (...a) => DEBUG && console.log("[zoom]", ...a);

  // Cache intrinsic size by src so we don't repeatedly load the same image
  const sizeCache = new Map(); // src -> Promise<{w,h}>

  function getActiveWrapperAndImg() {
    const selectedMedia =
      document.querySelector(".product-single__media.product-single__media--selected") ||
      document.querySelector(".product-single__media");

    if (!selectedMedia) return { wrapper: null, img: null };

    const img =
      selectedMedia.querySelector("img.product-single__photo") ||
      selectedMedia.querySelector("img");

    if (!img) return { wrapper: null, img: null };

    // Prefer known wrapper, otherwise fallback
    const wrapper =
      img.closest(".product-single__media__image") ||
      img.parentElement;

    return { wrapper, img };
  }

  function getBestSrc(img) {
    const src = img.getAttribute("data-zoom-img") || img.currentSrc || img.src || "";
    if (!src) return src;

    // Strip Shopify sizing suffix (_300x etc) if present
    return src.replace(/_(\d+)x(?=\.)/g, "");
  }

  function loadImageSize(src) {
    if (sizeCache.has(src)) return sizeCache.get(src);

    const p = new Promise((resolve) => {
      const im = new Image();
      im.decoding = "async";
      im.onload = () => resolve({ w: im.naturalWidth || 0, h: im.naturalHeight || 0 });
      im.onerror = () => resolve({ w: 0, h: 0 });
      im.src = src;
    });

    sizeCache.set(src, p);
    return p;
  }

  // Constrain overlay to the actual visible image rectangle inside the wrapper
  function fitOverlayToImg(wrapper, img, overlay) {
    const wRect = wrapper.getBoundingClientRect();
    const iRect = img.getBoundingClientRect();

    overlay.style.left = `${iRect.left - wRect.left}px`;
    overlay.style.top = `${iRect.top - wRect.top}px`;
    overlay.style.width = `${iRect.width}px`;
    overlay.style.height = `${iRect.height}px`;
  }

  async function refreshOverlay(img, overlay, wrapper) {
    const src = getBestSrc(img);
    if (!src) return;

    overlay.style.backgroundImage = `url("${src}")`;

    // Always size overlay to the image window (fixes “background becomes image”)
    fitOverlayToImg(wrapper, img, overlay);

    // Use the IMAGE rect for sizing math (not wrapper)
    const iRect = img.getBoundingClientRect();
    const Wc = iRect.width;
    const Hc = iRect.height;
    if (!Wc || !Hc) return;

    const { w: Wi, h: Hi } = await loadImageSize(src);

    if (!Wi || !Hi) {
      overlay.style.backgroundSize = "cover";
      return;
    }

    // Cover scaling relative to the IMAGE window
    const coverScale = Math.max(Wc / Wi, Hc / Hi);

    // Clamp zoom so we don't exceed native pixels (reduces fuzz)
    const maxZoom = Math.min(Wi / Wc, Hi / Hc);
    const zoom = Math.max(1, Math.min(ZOOM_SCALE_TARGET, maxZoom));

    const bgW = Wi * coverScale * zoom;
    const bgH = Hi * coverScale * zoom;

    overlay.style.backgroundSize = `${bgW}px ${bgH}px`;

    log("zoom:", zoom.toFixed(2), "src:", src);
  }

  function attachZoom(wrapper, img) {
    if (!wrapper || !img) return;

    let overlay = wrapper.querySelector(".zoom-overlay");

    if (!overlay) {
      wrapper.classList.add("zoomable-media");

      // Ensure transparency always renders as white *without editing the images*
      wrapper.style.backgroundColor = "#fff";

      overlay = document.createElement("div");
      overlay.className = "zoom-overlay";
      wrapper.appendChild(overlay);

      // Keep overlay local
      wrapper.style.position = "relative";
      wrapper.style.overflow = "hidden";

      overlay.style.position = "absolute";
      overlay.style.zIndex = "2";
      overlay.style.pointerEvents = "none";
      overlay.style.backgroundRepeat = "no-repeat";
      overlay.style.backgroundPosition = "50% 50%";
      overlay.style.backgroundColor = "#fff";

      // Show/hide only when hovering the IMAGE (not the big wrapper)
      img.addEventListener("mouseenter", () => {
        wrapper.classList.add("kf-zooming");
        refreshOverlay(img, overlay, wrapper);
      });

      img.addEventListener("mouseleave", () => {
        wrapper.classList.remove("kf-zooming");
        overlay.style.backgroundImage = "none"; // hard cleanup
      });

      // Smooth mousemove (relative to the IMAGE rect)
      let raf = 0;
      let lastEvent = null;

      wrapper.addEventListener("mousemove", (e) => {
        if (!wrapper.classList.contains("kf-zooming")) return;

        lastEvent = e;
        if (raf) return;

        raf = requestAnimationFrame(() => {
          raf = 0;

          // Always keep overlay aligned to the image window
          fitOverlayToImg(wrapper, img, overlay);

          // Position relative to the image rectangle (not wrapper)
          const r = img.getBoundingClientRect();
          const x = (lastEvent.clientX - r.left) / r.width;
          const y = (lastEvent.clientY - r.top) / r.height;
          const clamp = (v) => Math.max(0, Math.min(1, v));
          overlay.style.backgroundPosition = `${clamp(x) * 100}% ${clamp(y) * 100}%`;
        });
      });

      // If layout shifts while zooming (scroll/resize), keep it aligned
      window.addEventListener("scroll", () => {
        if (!wrapper.classList.contains("kf-zooming")) return;
        fitOverlayToImg(wrapper, img, overlay);
      }, { passive: true });

      window.addEventListener("resize", () => {
        if (!wrapper.classList.contains("kf-zooming")) return;
        refreshOverlay(img, overlay, wrapper);
      });

      log("overlay attached to wrapper:", wrapper);
    }

    // Refresh on init too
    refreshOverlay(img, overlay, wrapper);

    // Keep overlay synced if the image src changes (variants, lazyload)
    if (!img.dataset.zoomObserver) {
      img.dataset.zoomObserver = "1";
      new MutationObserver(() => refreshOverlay(img, overlay, wrapper)).observe(img, {
        attributes: true,
        attributeFilter: ["src", "srcset", "data-zoom-img", "style", "class"],
      });
    }
  }

  function init() {
    const { wrapper, img } = getActiveWrapperAndImg();
    log("found:", { wrapper, img });
    attachZoom(wrapper, img);
  }

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("shopify:section:load", init);

  // Re-init when thumbnails/selected media changes
  const medias = document.querySelector(".product-single__medias") || document.querySelector(".product-medias");
  if (medias) {
    new MutationObserver(() => init()).observe(medias, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  document.addEventListener("click", (e) => {
    if (e.target.closest(".product-single__medias, .product-medias")) {
      setTimeout(init, 30);
    }
  });
})();
